/**
 * @file signupFunnel.js
 * @description The one place that records the provider sign-up and setup funnel (owner 2026-10-29).
 *
 *   Guest steps go out with the device's anonymous id, so they join the earlier guest_apply_click / case_view rows:
 *     signup_form_viewed → signup_submitted → signup_failed | signup_succeeded
 *   Account steps go out with the user id, so they join profiles and applications:
 *     verify_login_prompt (guest: the verify link opened where nobody is signed in)
 *     onboarding_step_viewed → onboarding_finished → apply_resumed (CaseViewer) → the application
 *                                                 ↘ apply_resume_missed (the opening closed or is hidden meanwhile)
 *
 *   Metadata is low-cardinality only: role, step, a reason code, and the remembered case's id/code. Never an email,
 *   a name or anything typed.
 * @module lib/signupFunnel
 */
import { track, A } from "./analytics";

/** The remembered case (lib/signupIntent pending case) as event fields, or {} when there is none. */
function caseFields(pendingCase) {
  if (!pendingCase || !pendingCase.id) return {};
  return { case_id: pendingCase.id, case_code: pendingCase.code || null, case_action: pendingCase.action || "apply" };
}

/** Sign-up reasons we record. Anything else is "other" so a raw server message never lands in analytics.
 *  password_weak: the server refused a password that passed the form's checks, usually because it appears in a
 *  known data leak (Supabase leaked password protection). */
export const SIGNUP_FAIL_REASONS = ["invalid_email", "password_short", "password_weak", "email_exists", "terms", "server"];

/**
 * Sort a Supabase signUp error into what the form shows and records.
 * Mirrors the server's password rules (Supabase Auth: min length, character sets, leaked password check); the server
 * stays the enforcement. Returns { reason, leaked }: leaked is true when the password was found in a data leak.
 * @param {{ code?: string, message?: string, reasons?: string[] } | null} err
 */
export function classifySignupError(err) {
  const msg = String(err?.message || "");
  const reasons = Array.isArray(err?.reasons) ? err.reasons : [];
  if (err?.code === "weak_password" || /known to be weak|easy to guess|pwned/i.test(msg)) {
    const leaked = reasons.includes("pwned") || /known to be weak|easy to guess|pwned/i.test(msg);
    return { reason: "password_weak", leaked };
  }
  if (err?.code === "user_already_exists" || err?.code === "email_exists" || /already|registered|exists/i.test(msg)) {
    return { reason: "email_exists", leaked: false };
  }
  return { reason: "server", leaked: false };
}

let lastFormView = null; // { role, at } so a re-render or role tap within a second doesn't double count

/** The sign-up form is on screen (on mount, and when the visitor switches from Log in to Sign up). */
export function trackSignupFormViewed({ role, pendingCase, intent } = {}) {
  const now = Date.now();
  if (lastFormView && lastFormView.role === role && now - lastFormView.at < 1000) return;
  lastFormView = { role, at: now };
  track(A.SIGNUP_FORM_VIEWED, { supabaseMeta: { role: role || null, intent: intent || null, ...caseFields(pendingCase) } });
}

/** Create Account was tapped and passed the form's own checks. */
export function trackSignupSubmitted({ role, pendingCase } = {}) {
  track(A.SIGNUP_SUBMITTED, { supabaseMeta: { role: role || null, ...caseFields(pendingCase) } });
}

/** Create Account was tapped and stopped: by the form's checks or by the server. */
export function trackSignupFailed({ role, reason, pendingCase } = {}) {
  const r = SIGNUP_FAIL_REASONS.includes(reason) ? reason : "other";
  track(A.SIGNUP_FAILED, { supabaseMeta: { role: role || null, reason: r, ...caseFields(pendingCase) } });
}

/** The account was created (the verify screen or the app comes next). */
export function trackSignupSucceeded({ role, pendingCase } = {}) {
  track(A.SIGNUP_SUCCEEDED, { supabaseMeta: { role: role || null, ...caseFields(pendingCase) } });
}

/** A verify link opened where the account isn't signed in, so the person is asked to log in again. */
export function trackVerifyLoginPrompt() {
  track(A.VERIFY_LOGIN_PROMPT, { supabaseMeta: {} });
}

/** A setup step is on screen. */
export function trackOnboardingStep({ userId, role, step, pendingCase } = {}) {
  if (!userId) return;
  track(A.ONBOARDING_STEP_VIEWED, { userId, supabaseMeta: { role: role || null, step, ...caseFields(pendingCase) } });
}

/**
 * Setup saved. `how`: "complete" (all steps), "finish_later" (the skip prompt), "to_case" (straight back to the
 * remembered case after the basics).
 */
export function trackOnboardingFinished({ userId, role, step, how, pendingCase } = {}) {
  if (!userId) return;
  track(A.ONBOARDING_FINISHED, { userId, supabaseMeta: { role: role || null, step, how, ...caseFields(pendingCase) } });
}

/** The opening they signed up to apply to could not be reopened (closed, filled or hidden meanwhile). */
export function trackApplyResumeMissed({ userId, pendingCase } = {}) {
  if (!userId) return;
  track(A.APPLY_RESUME_MISSED, { userId, supabaseMeta: { ...caseFields(pendingCase) } });
}

/** Test-only: forget the double-count guard. */
export function _resetSignupFunnelForTests() { lastFormView = null; }
